<template>
   <AppLayout>
      <template #head>
         <AppHead
            :title="calendarEvent.data.title"
            :description="calendarEvent.data.seo_content"
            :image="calendarEvent.data.thumbnail?.seo_url ?? calendarEvent.data.thumbnail?.original_url"
            :url="route('events.view',{event: calendarEvent.data.slug})"
         />
      </template>
      <template #main>
         <div class="mx-auto max-w-7xl mb-24 overflow-hidden px-4 sm:px-6">
            <div
               class="flex flex-col bg-primary-gray-light p-4 mt-16 rounded-md items-center"
            >
               <!-- Banner Image -->
               <div
                  class="w-full h-96 group relative overflow-hidden cursor-pointer"
               >
                  <img
                     loading="lazy"
                     :srcset="calendarEvent.data.thumbnail?.original_url"
                     class="bg-cover bg-no-repeat bg-center rounded-lg h-full w-full object-cover object-center transform transition duration-500 group-hover:scale-105 group-hover:rounded-lg"
                     :alt="calendarEvent.data.title"
                  >
               </div>
               <!-- Dates -->
               <div
                  class="bg-white rounded-lg mt-4 p-4 sm:p-8 flex w-full justify-between"
               >
                  <div class="flex items-center">
                     <i class="pi pi-calendar text-primary-color" />
                     <div class="flex flex-col ml-2">
                        <p class="text-sm font-bold text-primary-gray">
                           {{ $t('misc.date') }}
                        </p>
                        <p class="text-sm text-primary-gray">
                           {{ eventDate }}
                        </p>
                     </div>
                  </div>
                  <div
                     class="flex items-center"
                     v-if="!isEmpty(calendarEvent.data.start_time) || !isEmpty(calendarEvent.data.start_time)"
                  >
                     <i class="pi pi-clock text-primary-color" />

                     <div
                        class="flex flex-col ml-2"
                     >
                        <p class="text-sm font-bold text-primary-gray">
                           {{ $t('misc.time') }}
                        </p>
                        <p class="text-sm text-primary-gray">
                           {{ calendarEvent.data.start_time }} -
                           {{ calendarEvent.data.end_time }}
                        </p>
                     </div>
                  </div>
               </div>
               <!-- Event details and booking -->
               <div
                  class="w-full flex flex-col bg-white lg:col-span-1 rounded-lg mt-8 p-4 sm:p-8"
               >
                  <span
                     v-if="calendarEvent.data.in_past"
                     class="text-center items-center rounded-md bg-yellow-100 px-2 py-1 text-md font-medium text-yellow-800"
                  >
                     {{ $t('events.this_event_has_ended') }}
                  </span>
                  <h1
                     class="text-4xl uppercase font-extrabold"
                     :class="{ 'mt-4': calendarEvent.data.in_past }"
                  >
                     {{ calendarEvent.data.title }}
                  </h1>

                  <div
                     class="mt-6 prose max-w-none text-sm post-content"
                     v-html="calendarEvent.data.content"
                  />
                  <div>
                     <p class="uppercase mt-8 font-bold">
                        {{ $t('events.share_event') }}
                     </p>
                     <div class="flex mt-2 space-x-1 items-center">
                        <ShareNetwork
                           network="facebook"
                           :title="calendarEvent.data.title"
                           :url="route('events.view',{event: calendarEvent.data.slug})"
                           :description="calendarEvent.data.content"
                           :media="
                              calendarEvent.data.thumbnail
                                 ?.original_url
                           "
                        >
                           <svg
                              class="h-8 w-8"
                              fill="#4267B2"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                           >
                              <path
                                 fill-rule="evenodd"
                                 d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                                 clip-rule="evenodd"
                              />
                           </svg>
                        </ShareNetwork>
                        <svg
                           v-if="false"
                           class="h-10 w-10"
                           viewBox="0 0 24 24"
                           id="google-calendar"
                        >
                           <g data-name="Layer 2">
                              <g data-name="Layer 3">
                                 <path
                                    fill="none"
                                    d="M0 0h24v24H0z"
                                 />
                                 <path
                                    fill="#fff"
                                    d="M1 12C1 4 4 1 12 1s11 3 11 11-3 11-11 11S1 20 1 12"
                                 />
                                 <path
                                    fill="#d9d9d9"
                                    d="M17.54 4.56H6.46A1.06 1.06 0 0 0 5.4 5.64v12.72a1.06 1.06 0 0 0 1.06 1.08h11.08a1.06 1.06 0 0 0 1.06-1.08V5.64a1.06 1.06 0 0 0-1.06-1.08Zm-8.6 1.68a.48.48 0 0 1 0-1 .48.48 0 0 1 0 1Zm6.12 0a.48.48 0 0 1 0-1 .48.48 0 1 1 0 1Z"
                                 />
                                 <path
                                    fill="#008dff"
                                    d="m4.85 18.23.49-5.3h13.32l.49 5.3a1.07 1.07 0 0 1-1.06 1.18H5.91a1.07 1.07 0 0 1-1.06-1.18Z"
                                 />
                                 <path
                                    fill="#0066e0"
                                    d="m4.57 7.93.77 5h13.32l.77-5a1.07 1.07 0 0 0-1.06-1.24H5.63a1.07 1.07 0 0 0-1.06 1.24Z"
                                 />
                                 <path
                                    fill="#fff"
                                    d="M11.68 16H7.93v-.38L10 13.34a5.45 5.45 0 0 0 .71-1 1.61 1.61 0 0 0 .2-.74 1.22 1.22 0 0 0-.33-.9 1.26 1.26 0 0 0-.92-.33 1.3 1.3 0 0 0-1 .39 1.36 1.36 0 0 0-.37 1H7.8a1.9 1.9 0 0 1 .2-.9 1.57 1.57 0 0 1 .65-.65 1.9 1.9 0 0 1 1-.24 1.77 1.77 0 0 1 1.28.43 1.54 1.54 0 0 1 .47 1.18 2.08 2.08 0 0 1-.26.92 7.26 7.26 0 0 1-.92 1.24l-1.68 1.89h3.14zm2.01-3.22h.51a1.76 1.76 0 0 0 .75-.15 1.14 1.14 0 0 0 .48-.41 1.13 1.13 0 0 0 .17-.6 1.19 1.19 0 0 0-.33-.9 1.26 1.26 0 0 0-.93-.33 1.33 1.33 0 0 0-.95.34 1.18 1.18 0 0 0-.37.89h-.49a1.58 1.58 0 0 1 .24-.85 1.55 1.55 0 0 1 .65-.59 2 2 0 0 1 .92-.21 1.81 1.81 0 0 1 1.28.44 1.63 1.63 0 0 1 .47 1.22 1.31 1.31 0 0 1-.27.8 1.7 1.7 0 0 1-.76.54 1.57 1.57 0 0 1 .85.54 1.48 1.48 0 0 1 .29.91 1.65 1.65 0 0 1-.5 1.24 1.9 1.9 0 0 1-1.34.46 2.24 2.24 0 0 1-1-.21 1.64 1.64 0 0 1-.7-.6 1.62 1.62 0 0 1-.24-.89h.49a1.22 1.22 0 0 0 .4.93 1.49 1.49 0 0 0 1 .36 1.41 1.41 0 0 0 1-.33 1.21 1.21 0 0 0 .36-1 1.1 1.1 0 0 0-.39-.91 1.73 1.73 0 0 0-1.13-.32h-.5z"
                                 />
                              </g>
                           </g>
                        </svg>
                     </div>
                  </div>
                  <div
                     class="w-full px-2 mt-6 border-t border-primary-color"
                  />
                  <div class="mt-6">
                     <div
                        v-if="calendarEvent.data.location_name"
                        class="flex items-center mb-6"
                     >
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke-width="1.5"
                           stroke="#138381"
                           class="w-12 h-12"
                        >
                           <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                           />
                           <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
                           />
                        </svg>
                        <div class="flex flex-col ml-2">
                           <p
                              class="text-sm font-bold text-primary-gray uppercase"
                           >
                              {{ $t('misc.location') }}
                           </p>
                           <p class="text-primary-gray">
                              {{ calendarEvent.data.location_name }}
                           </p>
                        </div>
                     </div>

                     <p class="mb-4 font-bold">
                        {{ $t('events.click_on_marker_to_get_directions') }}
                     </p>
                     <GoogleMap
                        ref="googleMap"
                        api-key="AIzaSyDpgrY6-AELQJsgqzDGvO6KgIjaxDT2pxw"
                        style="height: 20rem"
                        :center="calendarEvent.data.location_coordinates"
                        :zoom="19"
                        :options="{
                           zoomControl: true,
                           mapTypeControl: true,
                           scaleControl: true,
                           streetViewControl: false,
                           rotateControl: true,
                           fullscreenControl: true
                        }"
                     >
                        <Marker
                           @click="openMapDirections"
                           :options="{position: calendarEvent.data.location_coordinates}"
                        />
                     </GoogleMap>
                  </div>
                  <div
                     class="w-full px-2 mt-6 border-t border-primary-color"
                  />
                  <!--Booking form-->
                  <form
                     id="stepper_container"
                     v-if="showBookingForm"
                     class="flex-col mt-6"
                  >
                     <div class="border-b border-gray-900/10 pb-12">
                        <div
                           class="flex flex-col space-y-1"
                           v-if="isRegistrationOpen"
                        >
                           <h2 class="font-bold leading-7 uppercase">
                              {{ $t('events.book_now') }}
                           </h2>

                           <p
                              v-if="!isEmpty($page.props.auth.user) && isEmpty(userExistingBooking)"
                              class="text-sm leading-6 text-gray-600"
                           >
                              {{ $t('events.use_form_to_book') }}
                           </p>
                        </div>

                        <p
                           v-if="!isRegistrationOpen"
                           class="mt-1 text-sm sm:text-lg font-bold leading-6 uppercase"
                           :class="!registrationOpenDateIsFuture ? 'text-red-600' : 'text-black'"
                        >
                           {{ !registrationOpenDateIsFuture ?
                              $t(
                                 'events.registration_closed',
                              ): $t(
                                 'events.registration_opens',{
                                    date: dayjs(props.calendarEvent.data.event_data?.registration_open_date).format('DD-MM-YYYY')
                                 }
                              )
                           }}
                        </p>

                        <div
                           v-if="isEmpty($page.props.auth.user) || !isEmpty(userExistingBooking)"
                           class="mt-2 flex flex-col space-y-2 text-md"
                        >
                           <div v-if="isEmpty($page.props.auth.user)">
                              <div class="flex flex-col sm:flex-row space-x-0 sm:space-x-2 space-y-1 sm:space-y-0">
                                 <p>{{ $t('bookings.to_book_this_event_sign_in_required') }}</p>
                                 <Link
                                    :href="route('login',{redirect: 1})"
                                    class="font-semibold leading-6 text-primary-color-dark hover:text-primary-color"
                                 >
                                    {{ $t('account.sign_in_to_account') }}
                                 </Link>
                              </div>
                              <div class="flex mt-4 sm:mt-0 space-x-2">
                                 <p>{{ $t('account.not_a_member') }}</p>
                                 <Link
                                    :href="route('register',{redirect: 1})"
                                    class="font-semibold leading-6 text-primary-color-dark hover:text-primary-color"
                                 >
                                    {{ $t('account.register') }}
                                 </Link>
                              </div>
                           </div>
                           <div v-if="!isEmpty(userExistingBooking)">
                              <div class="flex space-x-1">
                                 <span>
                                    {{ $t('bookings.a_booking_was_already_made') }}
                                    <Link
                                       :href="route('bookings.show',{booking: userExistingBooking.reference_number})"
                                       class="font-semibold leading-6 text-primary-color-dark hover:text-primary-color hover-animation"
                                    >
                                       {{ $t('bookings.click_here') }}
                                    </Link>
                                 </span>
                              </div>
                           </div>
                        </div>

                        <div
                           v-else
                        >
                           <div
                              v-if="isRegistrationOpen"

                              class="card flex justify-center mt-10"
                           >
                              <Stepper
                                 v-model:value="activeStep"
                                 class="w-full"
                              >
                                 <StepList>
                                    <Step
                                       v-slot="{ activateCallback, value, a11yAttrs }"
                                       as-child
                                       :value="1"
                                    >
                                       <div
                                          class="flex flex-row flex-auto gap-2"
                                          v-bind="a11yAttrs.root"
                                       >
                                          <div
                                             class="bg-transparent border-0 inline-flex flex-col gap-2"
                                             @click="activateCallback"
                                             v-bind="a11yAttrs.header"
                                          >
                                             <span
                                                :class="[
                                                   'rounded-full border-2 w-12 h-12 inline-flex items-center justify-center',
                                                   { 'bg-primary-color text-white border-primary': value <= activeStep, 'border-surface-200 dark:border-surface-700': value > activeStep }
                                                ]"
                                             >
                                                <i class="pi pi-calendar" />
                                             </span>
                                          </div>
                                          <Divider />
                                       </div>
                                    </Step>
                                    <Step
                                       v-slot="{ activateCallback, value, a11yAttrs }"
                                       as-child
                                       :value="2"
                                    >
                                       <div
                                          class="flex flex-row flex-auto gap-2 pl-2"
                                          v-bind="a11yAttrs.root"
                                       >
                                          <div
                                             class="bg-transparent border-0 inline-flex flex-col gap-2"
                                             @click="activateCallback"
                                             v-bind="a11yAttrs.header"
                                          >
                                             <span
                                                :class="[
                                                   'rounded-full border-2 w-12 h-12 inline-flex items-center justify-center',
                                                   { 'bg-primary-color text-white border-primary': value <= activeStep, 'border-surface-200 dark:border-surface-700': value > activeStep }
                                                ]"
                                             >
                                                <i class="pi pi-id-card" />
                                             </span>
                                          </div>
                                          <Divider />
                                       </div>
                                    </Step>
                                    <Step
                                       v-slot="{ activateCallback, value, a11yAttrs }"
                                       as-child
                                       :value="3"
                                    >
                                       <div
                                          class="flex flex-col pl-2"
                                          v-bind="a11yAttrs.root"
                                       >
                                          <div
                                             class="bg-transparent border-0 inline-flex flex-col gap-2"
                                             @click="activateCallback"
                                             v-bind="a11yAttrs.header"
                                          >
                                             <span
                                                :class="[
                                                   'rounded-full border-2 w-12 h-12 inline-flex items-center justify-center',
                                                   { 'bg-primary-color text-white border-primary': value <= activeStep, 'border-surface-200 dark:border-surface-700': value > activeStep }
                                                ]"
                                             >
                                                <i class="pi pi-check" />
                                             </span>
                                          </div>
                                       </div>
                                    </Step>
                                 </StepList>
                                 <StepPanels>
                                    <StepPanel
                                       v-slot="{ activateCallback }"
                                       :value="1"
                                    >
                                       <div
                                          class="flex flex-col gap-2 mx-auto"
                                       >
                                          <div class="mt-4 mb-4 text-xl font-semibold">
                                             {{ $t('bookings.form_steps.step_1') }}
                                          </div>
                                          <div class="flex flex-col">
                                             <p
                                                v-if="
                                                   !isEmpty(
                                                      registrationCloseDate
                                                   ) && isRegistrationOpen
                                                "
                                                class="mt-1 text-sm font-extrabold leading-6 text-gray-600"
                                             >
                                                {{
                                                   $t(
                                                      'events.registration_closes',
                                                   )
                                                }}
                                                {{ registrationCloseDate }}
                                             </p>
                                             <p
                                                v-if="!isRegistrationOpen"
                                                class="mt-1 text-sm font-extrabold leading-6 text-red-600 uppercase"
                                             >
                                                {{
                                                   $t(
                                                      'events.registration_closed',
                                                   )
                                                }}
                                             </p>

                                             <!--Adult pricing-->
                                             <div class="flex">
                                                <h1
                                                   v-if="
                                                      !isEmpty(
                                                         calendarEvent.data
                                                            .event_data
                                                            ?.adult_price
                                                      )
                                                   "
                                                   class="mt-1 text-sm font-extrabold leading-6 text-gray-600"
                                                >
                                                   {{
                                                      $t(
                                                         'events.price_per_person',
                                                         {
                                                            price: calendarEvent.data.event_data?.adult_price > 0 ? formatEuros(
                                                               calendarEvent.data.event_data?.adult_price,
                                                            ) : $t('events.free'),
                                                         },
                                                      )
                                                   }}
                                                </h1>
                                             </div>
                                             <!--Underage pricing-->
                                             <div class="flex">
                                                <h1
                                                   v-if="
                                                      !isEmpty(
                                                         calendarEvent.data
                                                            .event_data
                                                            ?.underage_price
                                                      )
                                                   "
                                                   class="mt-1 text-sm font-extrabold leading-6 text-gray-600"
                                                >
                                                   {{
                                                      $t(
                                                         'events.price_per_underage',
                                                         {
                                                            price: calendarEvent.data.event_data?.underage_price > 0 ? formatEuros(
                                                               calendarEvent.data.event_data?.underage_price,
                                                            ) : $t('events.free'),
                                                         },
                                                      )
                                                   }}
                                                </h1>
                                             </div>

                                             <div class="mt-4">
                                                <InputLabel
                                                   class="required"
                                                   for="spots"
                                                   :value="
                                                      $t(
                                                         'events.select_spots'
                                                      )
                                                   "
                                                />
                                                <div class="mt-3" />
                                                <NumberPickerInput
                                                   v-model="guestCounter"
                                                   class="mt-2"
                                                   :min="1"
                                                   :max="10"
                                                >
                                                   <template #help-text>
                                                      <p
                                                         v-if="
                                                            eventHasSpotLimit &&
                                                               availableSpots >
                                                               0
                                                         "
                                                         class="mt-2 text-sm text-primary-gray"
                                                      >
                                                         {{
                                                            $t(
                                                               'events.available_spots',
                                                               {
                                                                  spots: availableSpots,
                                                               },
                                                            )
                                                         }}
                                                      </p>

                                                      <div
                                                         v-if="
                                                            guestsWillBeInWaitingList &&
                                                               availableSpots >
                                                               0
                                                         "
                                                         class="mt-2 text-red-600 text-sm text-primary-gray flex-col"
                                                      >
                                                         <p
                                                            v-if="
                                                               !bookingForm.join_as_group
                                                            "
                                                         >
                                                            {{
                                                               $t(
                                                                  'events.some_spots_are_guaranteed',
                                                                  {
                                                                     available_spots:
                                                                        availableSpots,
                                                                     spots: bookingForm.spots,
                                                                  },
                                                               )
                                                            }}
                                                         </p>
                                                         <p v-else>
                                                            {{ $t('bookings.all_guests_put_waiting_list', {number: bookingForm.spots}) }}
                                                         </p>
                                                      </div>
                                                      <div
                                                         v-if="guestsWillBeInWaitingList &&availableSpots <=0 "
                                                         class="mt-2 text-red-600 text-sm text-primary-gray flex-col"
                                                      >
                                                         <p>
                                                            {{
                                                               $t(
                                                                  'events.event_fully_booked',
                                                               )
                                                            }}
                                                         </p>
                                                         <p>
                                                            {{
                                                               $t(
                                                                  'events.placed_in_waiting_list',
                                                               )
                                                            }}
                                                         </p>
                                                      </div>
                                                   </template>
                                                   <template #max-text>
                                                      <p
                                                         class="mt-2 text-sm text-red-600"
                                                      >
                                                         {{
                                                            $t(
                                                               'events.max_booking_number',
                                                            )
                                                         }}
                                                      </p>
                                                   </template>
                                                </NumberPickerInput>
                                                <div
                                                   v-if="bookingForm.spots > 1"
                                                   class="flex flex-col"
                                                >
                                                   <div class="block mt-4">
                                                      <label
                                                         class="flex items-center"
                                                      >
                                                         <Checkbox
                                                            size="small"
                                                            binary
                                                            v-model="bookingForm.join_as_group"
                                                         />
                                                         <span
                                                            class="ms-2 text-md text-primary-gray"
                                                         >{{
                                                            $t(
                                                               'events.join_as_group',
                                                            )
                                                         }}</span>
                                                      </label>
                                                   </div>
                                                   <span
                                                      class="text-sm mt-2 text-gray-600"
                                                   >{{
                                                      $t(
                                                         'events.join_as_group_explanation',
                                                      )
                                                   }}</span>
                                                </div>
                                             </div>
                                          </div>
                                       </div>
                                       <div class="flex flex-col pt-6 items-end">
                                          <Divider />
                                          <Button
                                             label="Next"
                                             size="small"
                                             icon="pi pi-arrow-right"
                                             icon-pos="right"
                                             v-scroll-to="'#stepper_container'"
                                             @click="activateCallback(2)"
                                          />
                                       </div>
                                    </StepPanel>
                                    <StepPanel
                                       v-slot="{ activateCallback }"
                                       :value="2"
                                    >
                                       <div
                                          class="flex flex-col gap-2 mx-auto"
                                       >
                                          <div class="mt-4 text-xl font-semibold">
                                             {{ $t('bookings.form_steps.step_2') }}
                                          </div>
                                          <div class="flex-col gap-4">
                                             <div
                                                v-if="
                                                   !bookingForm.has_custom_form
                                                "
                                             >
                                                <BookingForm
                                                   class="mb-10"
                                                   @remove-guest="
                                                      removeGuestForm
                                                   "
                                                   v-for="count in guestCounter"
                                                   :key="count"
                                                   v-model="
                                                      bookingForm.guests[
                                                         count - 1
                                                      ]
                                                   "
                                                   :errors="bookingForm.errors"
                                                   :guest-index="count - 1"
                                                />
                                             </div>
                                             <div
                                                v-if="
                                                   bookingForm.has_custom_form
                                                "
                                             >
                                                <CustomBookingForm
                                                   class="mb-10"
                                                   @remove-guest="
                                                      removeGuestForm
                                                   "
                                                   v-for="count in guestCounter"
                                                   :key="count"
                                                   v-model="
                                                      bookingForm.guests[
                                                         count - 1
                                                      ]
                                                   "
                                                   :errors="bookingForm.errors"
                                                   :guest-index="count - 1"
                                                />
                                             </div>
                                             <div
                                                class="mb-2"
                                                v-if="(guestsAreMinors && isLoggedInUserMinor) || (!guestsAreMinors && isLoggedInUserMinor)"
                                             >
                                                {{ trans('account.underage_registration') }}
                                             </div>
                                          </div>
                                       </div>
                                       <div class="flex flex-col pt-6">
                                          <Divider />
                                          <div class="flex justify-between">
                                             <Button
                                                label="Back"
                                                size="small"
                                                severity="secondary"
                                                icon="pi pi-arrow-left"
                                                v-scroll-to="'#stepper_container'"
                                                @click="() => {
                                                   bookingForm.clearErrors();
                                                   activateCallback(1);
                                                }"
                                             />
                                             <Button
                                                size="small"
                                                label="Next"
                                                icon="pi pi-arrow-right"
                                                icon-pos="right"
                                                :loading="validateBookingLoading"
                                                @click="async () => {
                                                   const formIsValid = await validateBeforeBooking();
                                                   if (!formIsValid) {
                                                      return;
                                                   }
                                                   scrollToStepperElement('#stepper_container')
                                                   activateCallback(3);

                                                }"
                                             />
                                          </div>
                                       </div>
                                    </StepPanel>
                                    <StepPanel
                                       v-slot="{ activateCallback }"
                                       :value="3"
                                    >
                                       <div
                                          class="flex flex-col w-full gap-2 mx-auto"
                                       >
                                          <div class="mt-4 mb-4 text-xl font-semibold">
                                             {{ $t('events.summary') }}
                                          </div>
                                          <div class="flex flex-col">
                                             <p
                                                class="mt-4 text-md font-bold text-primary-gray"
                                             >
                                                {{
                                                   $t(
                                                      'events.booked_spots',
                                                      {
                                                         spots: bookingForm.spots,
                                                      },
                                                   )
                                                }}
                                             </p>
                                             <p
                                                class="mt-4 text-xl font-bold text-primary-gray"
                                             >
                                                {{
                                                   $t('events.guest_info')
                                                }}
                                             </p>
                                             <div
                                                v-if="
                                                   !bookingForm.has_custom_form
                                                "
                                                class="mt-4 text-md font-bold text-primary-gray"
                                             >
                                                <p
                                                   v-for="guest in bookingForm.guests"
                                                   :key="guest.id"
                                                >
                                                   {{ guest.first_name }} {{ guest.last_name }} -
                                                   {{ guest.phone_number }}
                                                </p>
                                             </div>
                                             <div
                                                v-if="
                                                   bookingForm.has_custom_form
                                                "
                                             >
                                                <CustomBookingForm
                                                   class="mb-10"
                                                   :read-only="true"
                                                   @remove-guest="
                                                      removeGuestForm
                                                   "
                                                   v-for="count in guestCounter"
                                                   :key="count"
                                                   v-model="
                                                      bookingForm.guests[
                                                         count - 1
                                                      ]
                                                   "
                                                   :errors="
                                                      bookingForm.errors
                                                   "
                                                   :guest-index="count - 1"
                                                />
                                             </div>
                                             <p
                                                v-if="
                                                   !isEmpty(
                                                      calendarEvent.data
                                                         .event_data
                                                   )
                                                "
                                                class="mt-4 text-md font-bold text-primary-gray"
                                             >
                                                {{
                                                   $t(
                                                      'events.total_booking_amount',
                                                      {
                                                         amount: totalBookingAmount > 0 ? formatEuros(
                                                            totalBookingAmount,
                                                         ) : $t('events.free'),
                                                      },
                                                   )
                                                }}
                                             </p>
                                             <div
                                                v-if="!isEmpty(
                                                   calendarEvent.data
                                                      .event_data
                                                )"
                                             >
                                                <div v-if="!isEmpty(calendarEvent.data.payment_terms)">
                                                   <p
                                                      class="mt-4 text-sm sm:text-md prose post-content"
                                                      v-html="calendarEvent.data.payment_terms"
                                                   />
                                                </div>
                                             </div>

                                             <div
                                                class="block mt-4"
                                             >
                                                <label
                                                   class="flex items-center"
                                                >
                                                   <Checkbox
                                                      size="small"
                                                      binary
                                                      v-model="
                                                         bookingForm.accept_consent
                                                      "
                                                   />
                                                   <span
                                                      class="ms-2 text-sm sm:text-md text-primary-gray"
                                                   >
                                                      {{
                                                         $t(
                                                            'events.payment_consent_form_checkbox_line_1',
                                                         )
                                                      }}
                                                      <button
                                                         @click.prevent="
                                                            consentDialogVisible = true
                                                         "
                                                         class="text-primary-color-dark hover:text-primary-color-dark"
                                                      >
                                                         {{
                                                            $t(
                                                               'events.consent_form',
                                                            )
                                                         }}
                                                      </button>
                                                      {{
                                                         $t(
                                                            'events.payment_consent_form_checkbox_line_2',
                                                         )
                                                      }}
                                                   </span>
                                                </label>
                                             </div>

                                             <div class="mt-2">
                                                <div>
                                                   <VueRecaptcha
                                                      ref="reCaptcha"
                                                      @verify="onVerifyCaptcha"
                                                      :sitekey="page.props.recaptcha_site_key"
                                                   />
                                                </div>
                                                <InputError
                                                   class="mt-2"
                                                   :message="
                                                      bookingForm.errors.g_recaptcha_response
                                                   "
                                                />
                                             </div>
                                          </div>
                                       </div>
                                       <div class="flex flex-col pt-6">
                                          <Divider />
                                          <div class="flex justify-between">
                                             <Button
                                                label="Back"
                                                size="small"
                                                v-scroll-to="'#stepper_container'"
                                                severity="secondary"
                                                icon="pi pi-arrow-left"
                                                @click="activateCallback(2)"
                                             />
                                             <Button
                                                :label="$t('events.book_now_button')"
                                                size="small"
                                                severity="primary"
                                                :loading="bookingForm.processing"
                                                @click="bookEvent"
                                             />
                                          </div>
                                       </div>
                                    </StepPanel>
                                 </StepPanels>
                              </Stepper>
                           </div>
                        </div>
                     </div>
                  </form>
               </div>
               <!--Event gallery-->
               <div
                  v-if="calendarEvent.data.images.length > 0 "
                  class="bg-white rounded-lg mt-4 p-8 flex flex-col w-full justify-between"
               >
                  <h2 class="py-8 text-4xl uppercase font-extrabold">
                     {{ $t('bookings.gallery') }}
                  </h2>
                  <lightgallery
                     :settings="{ speed: 500, plugins: plugins }"
                     :on-init="onInit"
                     :on-before-slide="onBeforeSlide"
                     class="py-8 mx-auto flex flex-col md:grid md:grid-cols-3 gap-3"
                  >
                     <a
                        :href="image.media_url"
                        class="group relative rounded overflow-hidden object-cover"
                        v-for="image in calendarEvent.data.images"
                        :key="image.id"
                     >
                        <img
                           loading="lazy"
                           :src="image.media_url"
                           alt=""
                           class="w-full transform transition duration-500 object-cover group-hover:scale-105"
                        >
                        <p
                           class="cursor-pointer absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center text-2xl text-center text-white font-roboto font-medium group-hover:bg-opacity-20 transition"
                        />
                     </a>
                  </lightgallery>
               </div>
            </div>
         </div>
         <Dialog
            v-model:visible="consentDialogVisible"
            modal
            :style="{ width: '30rem' }"
         >
            <template #container="{closeCallback}">
               <div
                  class="w-full h-full outline-none overflow-x-hidden px-2 sm:px-0"
               >
                  <div
                     class=" mx-auto relative w-auto"
                  >
                     <div
                        class="max-h-full overflow-hidden border-none relative flex flex-col w-full pointer-events-auto bg-white bg-clip-padding rounded-md outline-none text-current"
                     >
                        <div
                           class="flex flex-shrink-0 items-center justify-between p-4 border-b border-gray-200 rounded-t-md"
                        >
                           <div class="flex w-full items-center justify-items-center justify-between">
                              <h5 class="flex-1 text-lg font-semibold">
                                 {{ $t('bookings.consent_form') }}
                              </h5>
                              <div class="flex flex-shrink-0">
                                 <button
                                    type="button"
                                    @click="closeCallback"
                                    class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none "
                                 >
                                    <span class="sr-only">Close</span>
                                    <svg
                                       class="h-5 w-5"
                                       viewBox="0 0 20 20"
                                       fill="currentColor"
                                       aria-hidden="true"
                                    >
                                       <path
                                          d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
                                       />
                                    </svg>
                                 </button>
                              </div>
                           </div>
                        </div>
                        <div
                           class="flex-auto rounded-md overflow-y-scroll relative p-4 post-content prose-sm"
                           v-html="calendarEvent.data?.consent_form?.content"
                        />
                        <div
                           class="justify-end space-x-4 flex flex-shrink-0 flex-wrap items-center p-4 border-t border-gray-200"
                        >
                           <Button
                              size="small"
                              severity="contrast"
                              variant="text"
                              class="text-red-800 hover:text-black"
                              @click="() => {
                                 confirmOrDeclineConsentForm(false);
                                 closeCallback();
                              }"
                           >
                              {{ $t('misc.decline') }}
                           </Button>
                           <Button
                              size="small"
                              @click="() => {
                                 confirmOrDeclineConsentForm(true);
                                 closeCallback();
                              }"
                           >
                              {{ $t('misc.accept') }}
                           </Button>
                        </div>
                     </div>
                  </div>
               </div>
            </template>
         </Dialog>
      </template>
   </AppLayout>
</template>

<script setup>
   import AppLayout from '@/Layouts/AppLayout.vue';
   import { computed, ref, watch } from 'vue';
   import { filter, find, isEmpty, map, orderBy, property } from 'lodash';
   import { Link, useForm, usePage } from '@inertiajs/vue3';
   import BookingForm from '@/Components/Forms/BookingForm.vue';
   import { v4 as uuidv4 } from 'uuid';
   import NumberPickerInput from '@/Components/Forms/NumberPickerInput.vue';
   import InputLabel from '@/Components/Forms/InputLabel.vue';
   import { formatEuros } from '@/composables/useCurrency.js';
   import CustomBookingForm from '@/Components/Forms/CustomBookingForm.vue';
   import FormField from '@/Models/FormField.js';
   import { trans } from 'laravel-vue-i18n';
   import InputError from '@/Components/Forms/InputError.vue';
   import { VueRecaptcha } from 'vue-recaptcha';
   import Lightgallery from 'lightgallery/vue';
   import lgThumbnail from 'lightgallery/plugins/thumbnail';
   import lgZoom from 'lightgallery/plugins/zoom';
   import { GoogleMap, Marker } from 'vue3-google-map';
   import { formatEventDate } from '@/composables/useFormatsDateTime.js';
   import AppHead from '@/Components/Layout/AppHead.vue';
   import Stepper from 'primevue/stepper';
   import StepList from 'primevue/steplist';
   import StepPanels from 'primevue/steppanels';
   import Step from 'primevue/step';
   import StepPanel from 'primevue/steppanel';
   import { Button, Checkbox, Dialog, Divider } from 'primevue';
   import { scroller } from 'vue-scrollto/src/scrollTo';
   import dayjs from 'dayjs';
   import { useToast } from 'primevue/usetoast';

   const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
   const toast = useToast();
   const consentDialogVisible = ref(false);
   const scrollToStepperElement = scroller();
   const activeStep = ref(1);

   const plugins = ref([lgThumbnail, lgZoom]);
   const onInit = () => {};
   const onBeforeSlide = () => {};

   const page = usePage();
   const reCaptcha = ref();
   const googleMap = ref();

   const props = defineProps({
      calendarEvent: Object,
      userExistingBooking: Object,
   });

   const guestCounter = ref(1);

   const showBookingForm = computed(() => {
      if (props.calendarEvent.data.in_past) {
         return false;
      }
      return props.calendarEvent.data.requires_booking;
   });

   const eventHasSpotLimit = computed(
      () => props.calendarEvent.data.event_data?.total_spots,
   );


   const totalSpots = ref(props.calendarEvent.data.event_data?.total_spots);

   const getCustomGuestFields = (isPrimaryGuest = false) => {
      const formFields = props.calendarEvent.data.form.fields;

      const systemFields = filter(
         formFields,
         (field) => parseInt(field.is_system) === 1,
      );
      const otherFields = filter(
         formFields,
         (field) => parseInt(field.is_system) !== 1,
      );

      const systemSorted = map(
         orderBy(systemFields, ['order'], ['asc']),
         (field) => {
            let value = null;
            if (isPrimaryGuest) {
               if (field.name === 'email') {
                  value = page.props.auth.user?.email;
               }
               if (field.name === 'first_name') {
                  value = page.props.auth.user?.contact.first_name;
               }
               if (field.name === 'last_name') {
                  value = page.props.auth.user?.contact.last_name;
               }
               if (field.name === 'phone_number') {
                  value = page.props.auth.user?.contact?.phone_number;
               }
               if (field.name === 'date_of_birth') {
                  value = !isEmpty(page.props.auth.user?.contact?.dob) ? new Date(page.props.auth.user?.contact?.dob) : null;
               }
            }
            return new FormField(
               field.id,
               field.label,
               field.name,
               field.type,
               field.form_field_options,
               field.is_required,
               field.order,
               value,
            );
         },
      );
      if (isPrimaryGuest) {
         systemSorted.push(
            new FormField(
               1,
               'contact_id',
               'contact_id',
               'contact_id',
               null,
               false,
               4,
               page.props.auth.user?.contact.id,
            ),
         );
      }

      systemSorted.push(
         new FormField(
            1,
            'primary_guest',
            'primary_guest',
            'primary_guest',
            null,
            false,
            4,
            isPrimaryGuest,
         ),
      );

      const otherFieldsSorted = map(
         orderBy(otherFields, ['order'], ['asc']),
         (field) => {
            return new FormField(
               field.id,
               field.label,
               field.name,
               field.type,
               field.form_field_options,
               field.is_required,
               field.order,
               null,
            );
         },
      );

      return [...systemSorted, ...otherFieldsSorted];
   };

   const availableSpots = computed(() => {
      const calendarEvent = props.calendarEvent.data;

      const guests = calendarEvent.guests;
      if (isEmpty(guests)) {
         return totalSpots.value;
      }
      return totalSpots.value - guests.length;
   });

   const bookingForm = useForm(() => {
      const hasCustomForm = !isEmpty(props.calendarEvent.data.form);

      return {
         has_custom_form: hasCustomForm,
         spots: guestCounter.value,
         join_as_group: false,
         accept_consent: false,
         g_recaptcha_response: null,
         guests: [
            hasCustomForm ? getCustomGuestFields(true) : {
               id: uuidv4(),
               first_name: page.props.auth.user?.contact.first_name,
               last_name: page.props.auth.user?.contact.last_name,
               email: page.props.auth.user?.email,
               date_of_birth: !isEmpty(page.props.auth.user?.contact?.dob) ? new Date(page.props.auth.user?.contact?.dob) : null,
               is_waiting_list:
                  eventHasSpotLimit.value && availableSpots.value > 0 ? 0 : 1,
               phone_number: page.props.auth.user?.contact?.phone_number,
               primary_guest: true,
               contact_id: page.props.auth.user?.contact.id,
            },
         ],
      };
   });

   const guestsWillBeInWaitingList = computed(() => {
      return bookingForm.spots > availableSpots.value;
   });

   const removeGuestObjectFromForm = (index) => {
      bookingForm.guests = filter(bookingForm.guests, (guest) => {
         return guest.id !== index;
      });
   };

   const removeGuestForm = async (idToRemove) => {
      bookingForm.guests = filter(bookingForm.guests, (guest) => {
         return guest.id !== idToRemove;
      });

      guestCounter.value--;
   };
   const addGuestObjectToForm = (index) => {
      bookingForm.guests.push(
         isEmpty(props.calendarEvent.data.form) ? {
            id: index,
            first_name: null,
            last_name: null,
            email: null,
            is_waiting_list:
               eventHasSpotLimit.value && availableSpots.value > 0 ? 0 : 1,
            phone_number: null,
            primary_guest: false,
            contact_id: null,
         } : getCustomGuestFields(false),
      );
   };

   watch(guestCounter, (newValue, oldValue) => {
      bookingForm.spots = guestCounter.value;
      if (newValue > oldValue) {
         addGuestObjectToForm(newValue);
      }
      if (newValue < oldValue) {
         removeGuestObjectFromForm(oldValue);
      }
      if (bookingForm.spots <= 1) {
         bookingForm.join_as_group = false;
      }
   });

   const eventDate = computed(
      () => formatEventDate(props.calendarEvent.data.start_date, props.calendarEvent.data.end_date));

   const eventRequiresBooking = computed(() => props.calendarEvent.data.requires_booking);

   const registrationOpenDateIsFuture = computed(() => {
      if (isEmpty(props.calendarEvent.data.event_data?.registration_open_date)) {
         return false;
      }
      const registrationOpenDate = props.calendarEvent.data.event_data?.registration_open_date;

      return dayjs().startOf('day').isSame(dayjs(registrationOpenDate, 'YYYY-MM-DD').startOf('day')) ||
         dayjs(registrationOpenDate, 'YYYY-MM-DD').startOf('day').isAfter(dayjs().startOf('day'));
   });

   const isRegistrationOpen = computed(() => {
      if (!eventRequiresBooking.value) {
         return true;
      }
      if (isEmpty(props.calendarEvent.data.event_data?.registration_close_date)) {
         return true;
      }

      const closeData =
         props.calendarEvent.data.event_data.registration_close_date;

      if (dayjs().startOf('day').isAfter(dayjs(closeData, 'YYYY-MM-DD').startOf('day')) ||
         dayjs(closeData, 'YYYY-MM-DD').startOf('day').isSame(dayjs().startOf('day'))) {
         return false;
      }

      const registrationOpenDate = props.calendarEvent.data.event_data?.registration_open_date;

      if (isEmpty(registrationOpenDate)) {
         return true;
      }

      return dayjs().startOf('day').isSame(dayjs(registrationOpenDate, 'YYYY-MM-DD').startOf('day')) ||
         dayjs().startOf('day').isAfter(dayjs(registrationOpenDate, 'YYYY-MM-DD').startOf('day'));

   });


   const registrationCloseDate = computed(() => {
      if (!eventRequiresBooking.value) {
         return null;
      }
      if (isEmpty(props.calendarEvent.data?.event_data)) {
         return null;
      }
      const closeData =
         props.calendarEvent.data.event_data.registration_close_date;

      if (isEmpty(closeData)) {
         return null;
      }

      return dayjs(closeData, 'YYYY-MM-DD').format('DD MMMM YYYY');
   });

   const bookEvent = async () => {
      if (!bookingForm.accept_consent) {
         toast.add({
            group: 'headless',
            type: 'info',
            severity: 'custom',
            summary: 'Please accept the consent form to continue',
            life: 3000,
         });
         return;
      }
      return bookingForm.post(
         route('events.book', {event: props.calendarEvent.data.id}),
         {
            preserveScroll: false,
            onSuccess: (page) => {
               const toastData = page.props.flash;
               if (!isEmpty(toastData)) {
                  toast.add({
                     group: 'headless',
                     type: toastData.type,
                     severity: 'custom',
                     summary: toastData.title,
                     detail: toastData.message,
                     life: 3000,
                  });
               }
               guestCounter.value = 1;
               bookingForm.accept_consent = false;
               bookingForm.reset();
               reCaptcha.value.reset();
               activeStep.value = 1;
            },
            onError: (errors) => {
               reCaptcha.value.reset();
            },
            exception: (event) => {
               // todo handle error
            },
         },
      );
   };

   const validateGuestInfoForm = () => {
      let isFormValid = false;
      bookingForm.errors = {};

      if ((guestsAreMinors.value && isLoggedInUserMinor.value) || (!guestsAreMinors.value && isLoggedInUserMinor.value)) {
         return false;
      }

      bookingForm.guests.forEach((guest, index) => {
         if (bookingForm.has_custom_form) {
            guest.forEach((formField) => {
               if (formField.required && formField.type !== 'checkboxes') {
                  if (isEmpty(formField.value)) {
                     bookingForm.errors[`guests.${index}.${formField.id}`] =
                        trans('validation.field_required');
                     isFormValid = false;
                  } else {
                     isFormValid = true;
                  }
                  return;
               }

               if (formField.required && formField.type === 'checkboxes') {
                  const optionValues = map(formField.options, property('value'));
                  const selected = filter(
                     optionValues,
                     (value) => value === true,
                  );
                  if (isEmpty(selected)) {
                     bookingForm.errors[`guests.${index}.${formField.id}`] =
                        trans('validation.field_required');
                     isFormValid = false;
                  } else {
                     isFormValid = true;
                  }
               }
            });
            return isFormValid;
         }

         if (isEmpty(guest.first_name)) {
            bookingForm.errors[`guests.${index}.first_name`] = trans(
               'validation.field_required',
            );
            isFormValid = false;
         } else {
            isFormValid = true;
         }
         if (isEmpty(guest.last_name)) {
            bookingForm.errors[`guests.${index}.last_name`] = trans(
               'validation.field_required',
            );
            isFormValid = false;
         } else {
            isFormValid = true;
         }
         if (isEmpty(guest.phone_number)) {
            bookingForm.errors[`guests.${index}.phone_number`] = trans(
               'validation.field_required',
            );
            isFormValid = false;
         } else {
            isFormValid = true;

         }
      });
      return isFormValid;
   };

   const isLoggedInUserMinor = computed(() => {
      const contact = page.props.auth.user?.contact;

      if (isEmpty(contact)) {
         return false;
      }

      const age = dayjs().diff(dayjs(contact.dob, 'YYYY-MM-DD'), 'years');

      return age < 18;
   });

   const guestsAreMinors = computed(() => {
      const underagedGuest = find(bookingForm.guests, (guest) => {
         const dateOfBirth = guest?.date_of_birth;

         if (isEmpty(dateOfBirth)) {
            return false;
         }

         const age = dayjs().diff(dayjs(dateOfBirth, 'DD-MM-YYYY'), 'years');

         return age < 18;
      });

      if (isEmpty(underagedGuest)) {
         return false;
      }

      return !isEmpty(underagedGuest);
   });

   const totalBookingAmount = ref(0);

   const calculateTotalBookingAmount = () => {
      const eventData = props.calendarEvent.data.event_data;
      if (isEmpty(eventData)) {
         return null;
      }
      const adultPrice = eventData.adult_price;
      const underAgePrice = eventData.underage_price;

      const adults = filter(bookingForm.guests, (guest) => {
         let dateOfBirth = guest.date_of_birth;
         if (!isEmpty(props.calendarEvent.data.form)) {
            dateOfBirth = find(guest, (field) => field.name === 'date_of_birth')?.value;
         }
         return dayjs().diff(dayjs(dateOfBirth, 'DD-MM-YYYY'), 'years') >= 18;
      });
      const underAged = filter(bookingForm.guests, (guest) => {
         let dateOfBirth = guest.date_of_birth;
         if (!isEmpty(props.calendarEvent.data.form)) {
            dateOfBirth = find(guest, (field) => field.name === 'date_of_birth')?.value;
         }
         return dayjs().diff(dayjs(dateOfBirth, 'DD-MM-YYYY'), 'years') < 18;
      });

      const totalAdultPrice = adults.length * adultPrice;
      const totalUnderAgedPrice = underAged.length * underAgePrice;

      totalBookingAmount.value = totalAdultPrice + totalUnderAgedPrice;
   };

   const validateBookingLoading = ref(false);

   const validateBeforeBooking = async () => {
      validateBookingLoading.value = true;
      // if (!validateGuestInfoForm()) {
      //      scrollToStepperElement('#stepper_container')
      //    validateBookingLoading.value = false;
      //    return false;
      // }

      try {
         validateBookingLoading.value = true;
         bookingForm.clearErrors();
         await axios.post(
            route('events.book.validate', {
               event: props.calendarEvent.data.id,
            }),
            bookingForm,
         );
         validateBookingLoading.value = false;
         calculateTotalBookingAmount();
         return true;
      } catch (error) {
         validateBookingLoading.value = false;
         scrollToStepperElement('#stepper_container');
         const {response} = error;
         const {data} = response;
         const {errors} = data;

         bookingForm.errors = errors;

         return false;
      }
   };
   const confirmOrDeclineConsentForm = (accept) => {
      if (accept) {
         bookingForm.accept_consent = true;
         return;
      }
      bookingForm.accept_consent = false;
   };

   const onVerifyCaptcha = (response) => {
      bookingForm.g_recaptcha_response = response;
      bookingForm.errors.g_recaptcha_response = null;
   };

   const openMapDirections = () => {
      const urlSuffix = `${props.calendarEvent.data.location_coordinates.lat},${props.calendarEvent.data.location_coordinates.lng}`;

      window.open(
         `https://www.google.com/maps/search/?api=1&query=${urlSuffix}`,
         '_blank',
      );
   };


</script>

<style scoped>
@import url('https://cdn.jsdelivr.net/npm/lightgallery@2.0.0-beta.4/css/lightgallery.css');
@import url('https://cdn.jsdelivr.net/npm/lightgallery@2.0.0-beta.4/css/lg-zoom.css');
@import url('https://cdn.jsdelivr.net/npm/lightgallery@2.0.0-beta.4/css/lg-video.css');


</style>
